/**
 * @page ProzessTelegramPage
 * @route /prozesse/telegram
 * @description Telegram-Bot Verwaltung für Prozess-Management.
 *   3 Tabs: Bot-Konfiguration, Verifizierte User, Nachrichten-Log.
 * @roles admin, ops_manager
 * @i18n prozesse.telegram.*
 * @endpoints GET/PUT /api/prozesse/telegram/config, GET /api/prozesse/telegram/users,
 *   GET /api/prozesse/telegram/log, GET /api/prozesse/telegram/stats,
 *   POST /api/prozesse/telegram/webhook/setup, GET /api/prozesse/telegram/webhook/info,
 *   POST /api/prozesse/telegram/generate-code, DELETE /api/prozesse/telegram/users/:id
 */
import { useState, useEffect, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { ToggleSwitch } from '../components/ui';
import { MessageCircle, Settings, Users, ScrollText, Copy, Check, Trash2, RefreshCw, Loader2, ExternalLink, Key, Bot, Mic, Shield, AlertTriangle, Volume2, Play } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Tab Bar ──
function TabBar({ tabs, active, onSelect }) {
  return (
    <div className="flex gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => onSelect(tab.key)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
            active === tab.key
              ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)]'
              : 'text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary,#4B5563)]'
          }`}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  CONFIG TAB
// ══════════════════════════════════════════════════════════════

function ConfigTab({ t }) {
  const [config, setConfig] = useState(null);
  const [ttsConfig, setTtsConfig] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ttsSaving, setTtsSaving] = useState(false);
  const [form, setForm] = useState({});
  const [ttsForm, setTtsForm] = useState({});
  const [webhookUrl, setWebhookUrl] = useState('');
  const [setupMsg, setSetupMsg] = useState('');
  const [ttsTestStatus, setTtsTestStatus] = useState('');
  const [ttsTestLoading, setTtsTestLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, whRes, ttsRes] = await Promise.all([
        fetch(`${API}/api/prozesse/telegram/config`, { credentials: 'include' }),
        fetch(`${API}/api/prozesse/telegram/webhook/info`, { credentials: 'include' }),
        fetch(`${API}/api/prozesse/tts-config`, { credentials: 'include' }),
      ]);
      const cfg = await cfgRes.json();
      const wh = await whRes.json();
      const tts = await ttsRes.json();
      setConfig(cfg);
      setWebhookInfo(wh);
      setTtsConfig(tts);
      setForm({});
      setTtsForm({});
      if (wh.url) setWebhookUrl(wh.url);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/prozesse/telegram/config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      await load();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const saveTts = async () => {
    setTtsSaving(true);
    try {
      await fetch(`${API}/api/prozesse/tts-config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ttsForm),
      });
      await load();
    } catch { /* ignore */ }
    setTtsSaving(false);
  };

  const testTts = async () => {
    setTtsTestLoading(true);
    setTtsTestStatus('');
    try {
      const res = await fetch(`${API}/api/prozesse/tts-test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json();
        setTtsTestStatus(`error:${err.error || 'Fehler'}`);
        return;
      }
      const data = await res.json();
      const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
      audio.play();
      setTtsTestStatus('ok');
    } catch (err) {
      setTtsTestStatus(`error:${err.message}`);
    }
    setTtsTestLoading(false);
  };

  const setupWebhook = async () => {
    setSetupMsg('');
    try {
      const res = await fetch(`${API}/api/prozesse/telegram/webhook/setup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhook_url: webhookUrl }),
      });
      const data = await res.json();
      if (res.ok) { setSetupMsg('ok'); await load(); }
      else setSetupMsg(data.error || 'Fehler');
    } catch (err) { setSetupMsg(err.message); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[var(--text-tertiary)]" /></div>;

  const hasChanges = Object.keys(form).length > 0;
  const hasTtsChanges = Object.keys(ttsForm).length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Telegram Bot Card */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-4 h-4 text-[#338edc]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.telegram.botConfig')}</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">Bot Token</label>
            <input type="password" placeholder={config?.telegram_bot_token || 'Token eingeben...'} value={form.telegram_bot_token ?? ''}
              onChange={e => setForm(f => ({ ...f, telegram_bot_token: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
            />
          </div>

          <div>
            <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">Webhook Secret</label>
            <input type="password" placeholder={config?.telegram_webhook_secret || 'Optional...'} value={form.telegram_webhook_secret ?? ''}
              onChange={e => setForm(f => ({ ...f, telegram_webhook_secret: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('prozesse.telegram.botEnabled')}</label>
            <ToggleSwitch checked={!!(form.telegram_bot_enabled ?? config?.telegram_bot_enabled)} onChange={() => setForm(f => ({ ...f, telegram_bot_enabled: !(form.telegram_bot_enabled ?? config?.telegram_bot_enabled) }))} />
          </div>
        </div>

        {hasChanges && (
          <button onClick={save} disabled={saving}
            className="mt-4 px-4 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] disabled:opacity-50">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : t('prozesse.telegram.save')}
          </button>
        )}
      </div>

      {/* Whisper Card */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Mic className="w-4 h-4 text-[#338edc]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.telegram.whisperConfig')}</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">OpenAI API Key</label>
            <input type="password" placeholder={config?.whisper_api_key || 'sk-...'} value={form.whisper_api_key ?? ''}
              onChange={e => setForm(f => ({ ...f, whisper_api_key: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('prozesse.telegram.whisperEnabled')}</label>
            <ToggleSwitch checked={!!(form.whisper_enabled ?? config?.whisper_enabled)} onChange={() => setForm(f => ({ ...f, whisper_enabled: !(form.whisper_enabled ?? config?.whisper_enabled) }))} />
          </div>

          <p className="text-[10px] text-[var(--text-muted,#9CA3AF)]">
            {t('prozesse.telegram.whisperHint')}
          </p>
        </div>

        {hasChanges && (
          <button onClick={save} disabled={saving}
            className="mt-4 px-4 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] disabled:opacity-50">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : t('prozesse.telegram.save')}
          </button>
        )}
      </div>

      {/* ElevenLabs TTS Card */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg p-4 lg:col-span-2">
        <div className="flex items-center gap-2 mb-4">
          <Volume2 className="w-4 h-4 text-[#338edc]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.tts.title')}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">{t('prozesse.tts.apiKey')}</label>
              <input type="password" placeholder={ttsConfig?.elevenlabs_api_key || 'API Key eingeben...'} value={ttsForm.elevenlabs_api_key ?? ''}
                onChange={e => setTtsForm(f => ({ ...f, elevenlabs_api_key: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
              />
            </div>

            <div>
              <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">{t('prozesse.tts.voiceId')}</label>
              <input type="text" placeholder={ttsConfig?.elevenlabs_voice_id || 'Voice ID...'} value={ttsForm.elevenlabs_voice_id ?? ''}
                onChange={e => setTtsForm(f => ({ ...f, elevenlabs_voice_id: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
              />
            </div>

            <div>
              <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">{t('prozesse.tts.model')}</label>
              <select value={ttsForm.elevenlabs_model_id ?? ttsConfig?.elevenlabs_model_id ?? 'eleven_multilingual_v2'}
                onChange={e => setTtsForm(f => ({ ...f, elevenlabs_model_id: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
              >
                <option value="eleven_multilingual_v2">Multilingual v2</option>
                <option value="eleven_turbo_v2_5">Turbo v2.5</option>
                <option value="eleven_turbo_v2">Turbo v2</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[var(--text-secondary,#4B5563)]">{t('prozesse.tts.enabled')}</label>
              <ToggleSwitch checked={!!(ttsForm.elevenlabs_enabled ?? ttsConfig?.elevenlabs_enabled)} onChange={() => setTtsForm(f => ({ ...f, elevenlabs_enabled: !(ttsForm.elevenlabs_enabled ?? ttsConfig?.elevenlabs_enabled) }))} />
            </div>

            <div>
              <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-2">{t('prozesse.tts.mode')}</label>
              <div className="space-y-1.5">
                {[
                  { value: 'auto', label: t('prozesse.tts.modeAuto') },
                  { value: 'always', label: t('prozesse.tts.modeAlways') },
                  { value: 'never', label: t('prozesse.tts.modeNever') },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="voiceMode"
                      checked={(ttsForm.telegram_voice_response ?? ttsConfig?.telegram_voice_response ?? 'auto') === opt.value}
                      onChange={() => setTtsForm(f => ({ ...f, telegram_voice_response: opt.value }))}
                      className="w-3.5 h-3.5 text-[#338edc] accent-[#338edc]"
                    />
                    <span className="text-xs text-[var(--text-secondary,#4B5563)]">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button onClick={testTts} disabled={ttsTestLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--border-default,#E5E7EB)] rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50">
              {ttsTestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              {t('prozesse.tts.test')}
            </button>

            {ttsTestStatus === 'ok' && (
              <p className="text-xs text-green-600">{t('prozesse.tts.testSuccess')}</p>
            )}
            {ttsTestStatus.startsWith('error:') && (
              <p className="text-xs text-red-500">{t('prozesse.tts.testFailed')}: {ttsTestStatus.replace('error:', '')}</p>
            )}

            <p className="text-[10px] text-[var(--text-muted,#9CA3AF)]">
              {t('prozesse.tts.hint')}
            </p>
          </div>
        </div>

        {hasTtsChanges && (
          <button onClick={saveTts} disabled={ttsSaving}
            className="mt-4 px-4 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] disabled:opacity-50">
            {ttsSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : t('prozesse.telegram.save')}
          </button>
        )}
      </div>

      {/* Webhook Setup Card */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg p-4 lg:col-span-2">
        <div className="flex items-center gap-2 mb-4">
          <ExternalLink className="w-4 h-4 text-[#338edc]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.telegram.webhookSetup')}</h3>
        </div>

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">Webhook URL</label>
            <input type="text" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://dev.app.weship.eu/api/public/prozesse/telegram-webhook"
              className="w-full px-3 py-1.5 text-sm border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)]"
            />
          </div>
          <button onClick={setupWebhook}
            className="px-4 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] whitespace-nowrap">
            {t('prozesse.telegram.setupWebhook')}
          </button>
        </div>

        {setupMsg && (
          <p className={`mt-2 text-xs ${setupMsg === 'ok' ? 'text-green-600' : 'text-red-500'}`}>
            {setupMsg === 'ok' ? 'Webhook erfolgreich eingerichtet!' : setupMsg}
          </p>
        )}

        {webhookInfo && (
          <div className="mt-3 text-xs text-[var(--text-tertiary,#6B7280)] space-y-1">
            <p>Status: {webhookInfo.url ? 'Aktiv' : 'Nicht konfiguriert'}</p>
            {webhookInfo.url && <p>URL: {webhookInfo.url}</p>}
            {webhookInfo.pending_update_count > 0 && <p>Ausstehende Updates: {webhookInfo.pending_update_count}</p>}
            {webhookInfo.last_error_message && <p className="text-red-500">Fehler: {webhookInfo.last_error_message}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  USERS TAB
// ══════════════════════════════════════════════════════════════

function UsersTab({ t }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myCode, setMyCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/prozesse/telegram/users`, { credentials: 'include' });
      const usersData = await res.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateMyCode = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/prozesse/telegram/generate-code`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setMyCode(data.code);
    } catch { /* ignore */ }
    setGenerating(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(myCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const deleteUser = async (id) => {
    if (!confirm(t('prozesse.telegram.confirmDelete'))) return;
    await fetch(`${API}/api/prozesse/telegram/users/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  return (
    <div className="space-y-4">
      {/* Verification Code Generator */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-[#338edc]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.telegram.verification')}</h3>
        </div>
        <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-3">{t('prozesse.telegram.verificationHint')}</p>

        <div className="flex items-center gap-3">
          <button onClick={generateMyCode} disabled={generating}
            className="px-4 py-1.5 text-xs font-medium bg-[#338edc] text-white rounded-lg hover:bg-[#2a7bc8] disabled:opacity-50">
            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : t('prozesse.telegram.generateCode')}
          </button>

          {myCode && (
            <div className="flex items-center gap-2">
              <code className="px-3 py-1 bg-[var(--bg-inset,#F8F9FC)] rounded text-sm font-mono font-bold tracking-widest">{myCode}</code>
              <button onClick={copyCode} className="p-1 hover:bg-[var(--bg-inset)] rounded">
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-[var(--text-tertiary)]" />}
              </button>
              <span className="text-[10px] text-[var(--text-muted,#9CA3AF)]">{t('prozesse.telegram.codeExpiry')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--text-tertiary)]" /></div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
                <th className="px-3 py-2 text-left text-[var(--text-tertiary,#6B7280)] font-medium">Telegram</th>
                <th className="px-3 py-2 text-left text-[var(--text-tertiary,#6B7280)] font-medium">WeShip User</th>
                <th className="px-3 py-2 text-center text-[var(--text-tertiary,#6B7280)] font-medium">Status</th>
                <th className="px-3 py-2 text-left text-[var(--text-tertiary,#6B7280)] font-medium">{t('prozesse.telegram.activeProcess')}</th>
                <th className="px-3 py-2 text-right text-[var(--text-tertiary,#6B7280)] font-medium">{t('prozesse.telegram.messages')}</th>
                <th className="px-3 py-2 text-right text-[var(--text-tertiary,#6B7280)] font-medium">{t('prozesse.telegram.connectedSince')}</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[var(--text-tertiary)]">{t('prozesse.telegram.noUsers')}</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)]">
                  <td className="px-3 py-2">
                    <div className="font-medium text-[var(--text-primary,#1A1A2E)]">{u.telegram_first_name || '–'}</div>
                    <div className="text-[var(--text-muted,#9CA3AF)]">@{u.telegram_username || u.telegram_id}</div>
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary,#4B5563)]">{u.user_name || '–'}</td>
                  <td className="px-3 py-2 text-center">
                    {u.verifiziert ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#D1FAE5] text-[#10B981]">
                        <Shield className="w-3 h-3" /> {t('prozesse.telegram.verified')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FEF3C7] text-[#F59E0B]">
                        <AlertTriangle className="w-3 h-3" /> {t('prozesse.telegram.pending')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary,#4B5563)]">{u.prozess_titel || '–'}</td>
                  <td className="px-3 py-2 text-right text-[var(--text-primary,#1A1A2E)] font-medium">{u.nachrichten_count}</td>
                  <td className="px-3 py-2 text-right text-[var(--text-tertiary,#6B7280)]">{formatDate(u.erstellt_am)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => deleteUser(u.id)} className="p-1 hover:bg-red-50 rounded text-[var(--text-tertiary)] hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  LOG TAB
// ══════════════════════════════════════════════════════════════

function LogTab({ t }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/prozesse/telegram/log?limit=${limit}&offset=${page * limit}`, { credentials: 'include' });
      const data = await res.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit);

  const typIcon = (typ) => {
    switch (typ) {
      case 'command': return '⌘';
      case 'image': return '📸';
      case 'video': return '🎥';
      case 'document': return '📎';
      case 'text': return '💬';
      default: return '•';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{total} {t('prozesse.telegram.totalMessages')}</p>
        <button onClick={load} className="p-1.5 hover:bg-[var(--bg-inset)] rounded">
          <RefreshCw className={`w-3.5 h-3.5 text-[var(--text-tertiary)] ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !rows.length ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--text-tertiary)]" /></div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
                <th className="px-3 py-2 text-left text-[var(--text-tertiary)] font-medium w-10">{t('prozesse.telegram.direction')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-tertiary)] font-medium">User</th>
                <th className="px-3 py-2 text-left text-[var(--text-tertiary)] font-medium w-8">Typ</th>
                <th className="px-3 py-2 text-left text-[var(--text-tertiary)] font-medium">{t('prozesse.telegram.message')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-tertiary)] font-medium">{t('prozesse.telegram.process')}</th>
                <th className="px-3 py-2 text-right text-[var(--text-tertiary)] font-medium">{t('prozesse.telegram.time')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)]">
                  <td className="px-3 py-2">
                    <span className={`inline-block w-5 text-center text-[10px] font-medium rounded ${
                      r.richtung === 'eingehend' ? 'text-[#338edc]' : 'text-[#10B981]'
                    }`}>
                      {r.richtung === 'eingehend' ? '←' : '→'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary,#4B5563)]">
                    {r.telegram_first_name || r.telegram_username || r.telegram_id}
                  </td>
                  <td className="px-3 py-2 text-center">{typIcon(r.nachricht_typ)}</td>
                  <td className="px-3 py-2 text-[var(--text-primary,#1A1A2E)] max-w-xs truncate">{r.nachricht || '–'}</td>
                  <td className="px-3 py-2 text-[var(--text-tertiary,#6B7280)]">{r.prozess_titel || '–'}</td>
                  <td className="px-3 py-2 text-right text-[var(--text-tertiary,#6B7280)]">{formatDate(r.erstellt_am)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[var(--text-tertiary)]">{t('prozesse.telegram.noMessages')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-3 py-1 text-xs border rounded disabled:opacity-30">←</button>
          <span className="text-xs text-[var(--text-tertiary)]">{page + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            className="px-3 py-1 text-xs border rounded disabled:opacity-30">→</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function ProzessTelegramPage() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('config');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/prozesse/telegram/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const tabs = [
    { key: 'config', label: t('prozesse.telegram.tabConfig'), icon: <Settings className="w-3.5 h-3.5" /> },
    { key: 'users', label: t('prozesse.telegram.tabUsers'), icon: <Users className="w-3.5 h-3.5" /> },
    { key: 'log', label: t('prozesse.telegram.tabLog'), icon: <ScrollText className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="p-6 md:p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-[#338edc]" />
          <h1 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.telegram.title')}</h1>
        </div>
      </div>

      {/* KPI Bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: t('prozesse.telegram.kpiUsers'), value: stats.users_verifiziert, sub: `/ ${stats.users_total}` },
            { label: t('prozesse.telegram.kpiMessages'), value: stats.nachrichten_total },
            { label: t('prozesse.telegram.kpiMedia'), value: stats.medien_total },
            { label: t('prozesse.telegram.kpiToday'), value: stats.nachrichten_heute },
          ].map((kpi, i) => (
            <div key={i} className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-3">
              <p className="text-[10px] text-[var(--text-tertiary,#6B7280)] uppercase tracking-wide">{kpi.label}</p>
              <p className="text-lg font-bold text-[var(--text-primary,#1A1A2E)]">
                {kpi.value}{kpi.sub && <span className="text-xs font-normal text-[var(--text-muted,#9CA3AF)]"> {kpi.sub}</span>}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <TabBar tabs={tabs} active={tab} onSelect={setTab} />

      {/* Tab Content */}
      {tab === 'config' && <ConfigTab t={t} />}
      {tab === 'users' && <UsersTab t={t} />}
      {tab === 'log' && <LogTab t={t} />}
    </div>
  );
}
